
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export default function GoogleAnalyticsScript() {
  useEffect(() => {
    const removeExisting = () => {
      document
        .querySelectorAll('script[data-analytics="google"]')
        .forEach((el) => el.remove());
    };

    const injectScriptsFromCode = (rawCode: string) => {
      try {
        removeExisting();
        const code = rawCode.trim();
        const head = document.head;
        if (!head) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(code, 'text/html');
        const foundScripts = Array.from(doc.querySelectorAll('script'));

        if (foundScripts.length > 0) {
          // Insert in reverse to preserve original order at the very top of <head>
          for (let i = foundScripts.length - 1; i >= 0; i--) {
            const srcEl = foundScripts[i] as HTMLScriptElement;
            const s = document.createElement('script');
            s.setAttribute('data-analytics', 'google');

            // Copy safe attributes
            Array.from(srcEl.attributes).forEach((attr) => {
              const name = attr.name.toLowerCase();
              if (name.startsWith('on')) return; // avoid event handlers
              if (
                name === 'src' ||
                name === 'type' ||
                name === 'crossorigin' ||
                name === 'referrerpolicy' ||
                name === 'async' ||
                name === 'defer' ||
                name.startsWith('data-')
              ) {
                if (name === 'async') s.async = true;
                else if (name === 'defer') s.defer = true;
                else s.setAttribute(attr.name, attr.value);
              }
            });

            // Copy src and/or inline content
            if (srcEl.src) s.src = srcEl.src;
            if (srcEl.innerHTML) s.innerHTML = srcEl.innerHTML;

            // Place at the very beginning of <head>
            head.insertBefore(s, head.firstChild);
          }
        } else {
          // No <script> tag in snippet, inject as inline script
          const s = document.createElement('script');
          s.setAttribute('data-analytics', 'google');
          s.innerHTML = code;
          head.insertBefore(s, head.firstChild);
        }
        console.log('Google Analytics script loaded successfully');
      } catch (e) {
        console.error('Error injecting Google Analytics script:', e);
      }
    };

    const loadAnalyticsScript = async () => {
      try {
        const { data, error } = await supabase
          .from('site_analytics')
          .select('code, enabled')
          .eq('type', 'google_analytics')
          .eq('enabled', true)
          .maybeSingle();

        if (error) {
          console.error('Error fetching analytics code:', error);
          return;
        }

        if (data && data.code && data.enabled) {
          injectScriptsFromCode(data.code);
        } else {
          // If disabled or not found, ensure scripts are removed
          removeExisting();
        }
      } catch (error) {
        console.error('Failed to load Google Analytics script:', error);
      }
    };

    loadAnalyticsScript();

    const onUpdated = () => loadAnalyticsScript();
    window.addEventListener('site-analytics-updated', onUpdated);

    return () => {
      window.removeEventListener('site-analytics-updated', onUpdated);
      removeExisting();
    };
  }, []);

  return null; // This component doesn't render anything
}
